/**
 * Build-Your-Own customization wizard for in-store (QR table) ordering.
 * Steps the customer through each option group, enforces min/max picks,
 * and emits the resolved selections to the parent.
 */
import React, { useMemo, useState } from 'react';
import { Product, OptionGroup, ProductOption, SelectedOptionPick } from '../../types/menu';
import { getImageUrl } from '../../config/api';

interface BuildYourOwnSheetProps {
    product: Product;
    currency: string;
    onClose: () => void;
    onConfirm: (payload: {
        quantity: number;
        notes: string;
        selectedOptions: SelectedOptionPick[];
        extraPricePerUnit: number;
    }) => void;
}

const optionLabel = (opt: ProductOption): string => {
    if (opt.custom_label && opt.custom_label.trim()) return opt.custom_label;
    return opt.linked_product_name || 'Option';
};

const BuildYourOwnSheet: React.FC<BuildYourOwnSheetProps> = ({
    product,
    currency,
    onClose,
    onConfirm,
}) => {
    const groups = useMemo<OptionGroup[]>(() => {
        const list = (product.option_groups || []).slice();
        list.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        return list;
    }, [product.option_groups]);

    const [step, setStep] = useState(0);
    const [reviewing, setReviewing] = useState(false);
    const [selections, setSelections] = useState<Record<number, number[]>>({});
    const [quantity, setQuantity] = useState(1);
    const [notes, setNotes] = useState('');

    const totalSteps = groups.length;
    const currentGroup = groups[step];

    const isGroupValid = (group: OptionGroup): boolean => {
        if (!group) return true;
        const picks = selections[group.id]?.length || 0;
        if (group.is_required && picks < 1) return false;
        if (picks < group.min_picks) return false;
        if (picks > group.max_picks) return false;
        return true;
    };

    const allGroupsValid = groups.every(isGroupValid);

    const togglePick = (group: OptionGroup, optionId: number) => {
        setSelections((prev) => {
            const current = prev[group.id] || [];
            const isSelected = current.includes(optionId);
            if (group.max_picks === 1) {
                return { ...prev, [group.id]: isSelected ? [] : [optionId] };
            }
            if (isSelected) {
                return { ...prev, [group.id]: current.filter((id) => id !== optionId) };
            }
            if (current.length >= group.max_picks) {
                return prev;
            }
            return { ...prev, [group.id]: [...current, optionId] };
        });
    };

    const resolvedPicks = useMemo<SelectedOptionPick[]>(() => {
        const out: SelectedOptionPick[] = [];
        groups.forEach((group) => {
            const ids = selections[group.id] || [];
            ids.forEach((id) => {
                const opt = group.options.find((o) => o.id === id);
                if (!opt) return;
                out.push({
                    group_id: group.id,
                    group_name: group.name,
                    option_id: opt.id,
                    option_label: optionLabel(opt),
                    extra_price: Number(opt.extra_price) || 0,
                });
            });
        });
        return out;
    }, [groups, selections]);

    const extraPricePerUnit = resolvedPicks.reduce((s, p) => s + p.extra_price, 0);
    const basePrice = Number(product.price) || 0;
    const unitPrice = basePrice + extraPricePerUnit;
    const totalPrice = unitPrice * quantity;

    const handleNext = () => {
        if (currentGroup && !isGroupValid(currentGroup)) return;
        if (step + 1 < totalSteps) {
            setStep(step + 1);
        } else {
            setReviewing(true);
        }
    };

    const handleBack = () => {
        if (reviewing) {
            setReviewing(false);
            return;
        }
        if (step > 0) setStep(step - 1);
    };

    const handleConfirm = () => {
        if (!allGroupsValid) return;
        onConfirm({
            quantity,
            notes,
            selectedOptions: resolvedPicks,
            extraPricePerUnit,
        });
    };

    const progressPct = reviewing
        ? 100
        : totalSteps === 0
            ? 0
            : ((step + (isGroupValid(currentGroup) ? 1 : 0)) / totalSteps) * 100;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.85)',
                backdropFilter: 'blur(10px)',
                zIndex: 110,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
            }}
        >
            <div
                className="glass-dark cart-modal"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '92vh',
                    borderRadius: '1.5rem 1.5rem 0 0',
                    padding: '1.25rem 1.5rem 1rem',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '0.75rem',
                    }}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                            style={{
                                color: '#FFD700',
                                fontSize: '0.7rem',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontWeight: 700,
                            }}
                        >
                            Build Your Own
                        </div>
                        <h2
                            style={{
                                color: 'white',
                                fontSize: '1.35rem',
                                fontWeight: 700,
                                marginTop: '0.15rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {product.name}
                        </h2>
                        {!reviewing && totalSteps > 0 && (
                            <div
                                style={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '0.75rem',
                                    marginTop: '0.15rem',
                                }}
                            >
                                Step {step + 1} of {totalSteps}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: 'rgba(255,255,255,0.08)',
                            border: 'none',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            color: 'white',
                            fontSize: '1.4rem',
                            cursor: 'pointer',
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Progress bar */}
                {totalSteps > 0 && (
                    <div
                        style={{
                            height: 6,
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: 999,
                            overflow: 'hidden',
                            marginBottom: '1rem',
                        }}
                    >
                        <div
                            style={{
                                height: '100%',
                                width: `${progressPct}%`,
                                background: 'linear-gradient(90deg, #FFD700, #ff8c00)',
                                transition: 'width 0.25s ease',
                            }}
                        />
                    </div>
                )}

                {/* Body */}
                <div style={{ flex: 1, marginBottom: '1rem' }}>
                    {reviewing ? (
                        <ReviewView
                            product={product}
                            currency={currency}
                            groups={groups}
                            selections={selections}
                            quantity={quantity}
                            setQuantity={setQuantity}
                            notes={notes}
                            setNotes={setNotes}
                            extraPricePerUnit={extraPricePerUnit}
                        />
                    ) : currentGroup ? (
                        <GroupStep
                            group={currentGroup}
                            currency={currency}
                            selectedIds={selections[currentGroup.id] || []}
                            onToggle={(id) => togglePick(currentGroup, id)}
                        />
                    ) : (
                        <div
                            style={{
                                textAlign: 'center',
                                color: 'rgba(255,255,255,0.6)',
                                padding: '2.5rem 0',
                            }}
                        >
                            No options to configure.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    style={{
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        paddingTop: '0.75rem',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.75rem',
                            color: 'white',
                        }}
                    >
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                            {reviewing ? 'Total' : 'Base + extras'}
                        </span>
                        <span style={{ color: '#FFD700', fontSize: '1.15rem', fontWeight: 700 }}>
                            {Math.round(reviewing ? totalPrice : unitPrice)} {currency}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {(step > 0 || reviewing) && (
                            <button
                                onClick={handleBack}
                                style={{
                                    flex: 1,
                                    padding: '0.85rem',
                                    borderRadius: '0.75rem',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    background: 'rgba(255,255,255,0.05)',
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Back
                            </button>
                        )}
                        {!reviewing ? (
                            <button
                                onClick={handleNext}
                                disabled={currentGroup ? !isGroupValid(currentGroup) : false}
                                style={{
                                    flex: 1,
                                    padding: '0.85rem',
                                    borderRadius: '0.75rem',
                                    border: 'none',
                                    background:
                                        currentGroup && !isGroupValid(currentGroup)
                                            ? 'rgba(139, 0, 0, 0.4)'
                                            : 'rgba(139, 0, 0, 0.95)',
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    cursor:
                                        currentGroup && !isGroupValid(currentGroup)
                                            ? 'not-allowed'
                                            : 'pointer',
                                }}
                            >
                                {step + 1 < totalSteps ? 'Next' : 'Review'}
                            </button>
                        ) : (
                            <button
                                onClick={handleConfirm}
                                disabled={!allGroupsValid}
                                style={{
                                    flex: 1,
                                    padding: '0.85rem',
                                    borderRadius: '0.75rem',
                                    border: 'none',
                                    background: !allGroupsValid
                                        ? 'rgba(139, 0, 0, 0.4)'
                                        : 'rgba(139, 0, 0, 0.95)',
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    cursor: !allGroupsValid ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Add to Cart · {Math.round(totalPrice)} {currency}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Sub-views ────────────────────────────────────────────────────────────

function GroupStep({
    group,
    currency,
    selectedIds,
    onToggle,
}: {
    group: OptionGroup;
    currency: string;
    selectedIds: number[];
    onToggle: (optionId: number) => void;
}) {
    const isSinglePick = group.max_picks === 1;
    const picksRequiredText =
        group.min_picks === group.max_picks
            ? `Pick exactly ${group.min_picks}`
            : `Pick ${group.min_picks}\u2013${group.max_picks}`;

    const availableOptions = group.options.filter(
        (o) => Number(o.is_available) === 1 || o.is_available === true
    );

    return (
        <div>
            <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <h3 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 700 }}>{group.name}</h3>
                    {group.is_required ? (
                        <span
                            style={{
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                color: '#fca5a5',
                                background: 'rgba(220, 38, 38, 0.2)',
                                padding: '0.15rem 0.5rem',
                                borderRadius: 999,
                            }}
                        >
                            Required
                        </span>
                    ) : (
                        <span
                            style={{
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                color: 'rgba(255,255,255,0.6)',
                                background: 'rgba(255,255,255,0.08)',
                                padding: '0.15rem 0.5rem',
                                borderRadius: 999,
                            }}
                        >
                            Optional
                        </span>
                    )}
                </div>
                {group.description && (
                    <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem' }}>
                        {group.description}
                    </p>
                )}
                <div style={{ color: '#FFD700', fontSize: '0.75rem', fontWeight: 600, marginTop: '0.25rem' }}>
                    {picksRequiredText} · {selectedIds.length} selected
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {availableOptions.map((opt) => {
                    const isSelected = selectedIds.includes(opt.id);
                    const extra = Number(opt.extra_price) || 0;
                    const img = opt.linked_product_image
                        ? getImageUrl(opt.linked_product_image)
                        : null;
                    const atMax =
                        !isSinglePick && !isSelected && selectedIds.length >= group.max_picks;

                    return (
                        <button
                            key={opt.id}
                            type="button"
                            disabled={atMax}
                            onClick={() => onToggle(opt.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem',
                                borderRadius: '0.75rem',
                                border: isSelected
                                    ? '2px solid #FFD700'
                                    : '2px solid rgba(255,255,255,0.1)',
                                background: isSelected
                                    ? 'rgba(255, 215, 0, 0.12)'
                                    : 'rgba(255,255,255,0.04)',
                                color: 'white',
                                textAlign: 'left',
                                cursor: atMax ? 'not-allowed' : 'pointer',
                                opacity: atMax ? 0.4 : 1,
                                transition: 'all 0.15s',
                                width: '100%',
                            }}
                        >
                            {img && (
                                <img
                                    src={img}
                                    alt=""
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 10,
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600 }}>{optionLabel(opt)}</div>
                                {extra > 0 && (
                                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                                        + {Math.round(extra)} {currency}
                                    </div>
                                )}
                            </div>
                            <div
                                style={{
                                    flexShrink: 0,
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    border: isSelected
                                        ? '2px solid #FFD700'
                                        : '2px solid rgba(255,255,255,0.3)',
                                    background: isSelected ? '#FFD700' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#0a0a0a',
                                    fontWeight: 900,
                                    fontSize: 12,
                                }}
                            >
                                {isSelected ? '✓' : ''}
                            </div>
                        </button>
                    );
                })}
                {availableOptions.length === 0 && (
                    <div
                        style={{
                            color: 'rgba(255,255,255,0.5)',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            padding: '1.5rem 0',
                        }}
                    >
                        No options available right now.
                    </div>
                )}
            </div>
        </div>
    );
}

function ReviewView({
    product,
    currency,
    groups,
    selections,
    quantity,
    setQuantity,
    notes,
    setNotes,
    extraPricePerUnit,
}: {
    product: Product;
    currency: string;
    groups: OptionGroup[];
    selections: Record<number, number[]>;
    quantity: number;
    setQuantity: (n: number) => void;
    notes: string;
    setNotes: (s: string) => void;
    extraPricePerUnit: number;
}) {
    const basePrice = Number(product.price) || 0;
    return (
        <div>
            <h3 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.75rem' }}>
                Review your selections
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                {groups.map((group) => {
                    const ids = selections[group.id] || [];
                    const picked: ProductOption[] = [];
                    ids.forEach((id) => {
                        const opt = group.options.find((o) => o.id === id);
                        if (opt) picked.push(opt);
                    });
                    return (
                        <div
                            key={group.id}
                            className="glass"
                            style={{
                                padding: '0.65rem 0.75rem',
                                borderRadius: '0.65rem',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '0.65rem',
                                    color: 'rgba(255,255,255,0.55)',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                    marginBottom: '0.25rem',
                                }}
                            >
                                {group.name}
                            </div>
                            {picked.length === 0 ? (
                                <div style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                    None
                                </div>
                            ) : (
                                <ul
                                    style={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: 0,
                                        color: 'white',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    {picked.map((opt) => {
                                        const extra = Number(opt.extra_price) || 0;
                                        return (
                                            <li
                                                key={opt.id}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.15rem 0',
                                                }}
                                            >
                                                <span>{optionLabel(opt)}</span>
                                                {extra > 0 && (
                                                    <span style={{ color: 'rgba(255,255,255,0.65)' }}>
                                                        + {Math.round(extra)} {currency}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
                <label
                    style={{
                        display: 'block',
                        color: 'white',
                        fontSize: '0.8rem',
                        marginBottom: '0.35rem',
                    }}
                >
                    Notes (optional)
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Any special instructions?"
                    style={{
                        width: '100%',
                        padding: '0.6rem 0.75rem',
                        borderRadius: '0.6rem',
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'white',
                        fontSize: '0.9rem',
                        resize: 'none',
                    }}
                />
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    paddingTop: '0.75rem',
                }}
            >
                <span style={{ color: 'white', fontWeight: 600 }}>Quantity</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                    <button
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(255,255,255,0.05)',
                            color: 'white',
                            fontSize: '1.1rem',
                            cursor: 'pointer',
                        }}
                    >
                        −
                    </button>
                    <span style={{ color: 'white', minWidth: 24, textAlign: 'center', fontWeight: 700 }}>
                        {quantity}
                    </span>
                    <button
                        type="button"
                        onClick={() => setQuantity(quantity + 1)}
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(255,255,255,0.05)',
                            color: 'white',
                            fontSize: '1.1rem',
                            cursor: 'pointer',
                        }}
                    >
                        +
                    </button>
                </div>
            </div>

            <div style={{ marginTop: '0.6rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Base price</span>
                    <span>
                        {Math.round(basePrice)} {currency}
                    </span>
                </div>
                {extraPricePerUnit > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Add-ons</span>
                        <span>
                            {Math.round(extraPricePerUnit)} {currency}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BuildYourOwnSheet;
